import React, { useState, useEffect } from 'react';
import { FaFileDownload, FaUsers, FaMoneyBillWave, FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import declaracionRentaService from '../../services/declaracionRentaService';
import sociosDividendosService from '../../services/sociosDividendosService';
import '../../styles/components/DJ1948RetirosDividendosTab.css';

/**
 * Componente para DJ1948 - Declaración de Retiros y Dividendos
 *
 * Muestra resumen de datos (socios, dividendos, retiros en exceso)
 * y permite generar/descargar el CSV para el SII.
 * Los datos se gestionan en /socios-dividendos.
 */
const DJ1948RetirosDividendosTab = ({
    declaracionId,
    year,
    idCompany,
    rutEmpresa,
    token
}) => {
    const [socios, setSocios] = useState([]);
    const [dividendos, setDividendos] = useState([]);
    const [retirosExceso, setRetirosExceso] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        loadData();
    }, [year]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [sociosData, dividendosData, retirosData] = await Promise.all([
                sociosDividendosService.getSocios(idCompany, token),
                sociosDividendosService.getDividendos(idCompany, year, token),
                sociosDividendosService.getRetirosExceso(idCompany, year, token)
            ]);
            setSocios(sociosData || []);
            setDividendos(dividendosData || []);
            setRetirosExceso(retirosData || []);
        } catch (error) {
            console.error('Error al cargar datos DJ1948:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cargar los datos de socios y dividendos'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadCSV = async () => {
        if (socios.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Sin socios',
                text: 'Debe registrar al menos un socio en la sección Socios empresa y Dividendos antes de generar el CSV'
            });
            return;
        }

        if (dividendos.length === 0) {
            const result = await Swal.fire({
                icon: 'info',
                title: 'Sin retiros ni dividendos',
                html: 'No hay retiros ni dividendos registrados para el año ' + year + '.<br/><br/>Se generará el CSV con los datos de los socios y montos en cero.',
                showCancelButton: true,
                confirmButtonText: 'Generar CSV',
                cancelButtonText: 'Cancelar'
            });

            if (!result.isConfirmed) return;
        }

        try {
            setDownloading(true);
            await declaracionRentaService.downloadAndSaveDJ1948CSV(
                idCompany,
                rutEmpresa,
                year,
                token
            );

            Swal.fire({
                icon: 'success',
                title: 'Descarga exitosa',
                text: 'El archivo CSV DJ1948 se ha descargado correctamente'
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al generar el CSV'
            });
        } finally {
            setDownloading(false);
        }
    };

    const formatNumber = (num) => {
        if (!num) return '$0';
        return '$' + Number(num).toLocaleString('es-CL');
    };

    // Calcular totales
    const totalMontoC5 = dividendos.reduce((sum, d) => sum + Number(d.monto_c5 || 0), 0);
    const totalMontoC8 = dividendos.reduce((sum, d) => sum + Number(d.monto_c8 || 0), 0);
    const totalAcciones = socios.reduce((sum, s) => sum + Number(s.cantidad_acciones || 0), 0);

    if (loading) {
        return <div className="dj1948-loading">Cargando datos...</div>;
    }

    return (
        <div className="dj1948-tab">
            <div className="dj1948-header">
                <h3>DJ 1948 - Declaración de Retiros y Dividendos - Año {year}</h3>
                <p className="dj1948-subtitle">
                    Los datos se gestionan desde la sección <strong>Socios empresa y Dividendos</strong> del menú principal.
                </p>
            </div>

            {/* Resumen en tarjetas */}
            <div className="dj1948-cards">
                <div className="dj1948-card">
                    <div className="dj1948-card-icon"><FaUsers /></div>
                    <div className="dj1948-card-info">
                        <span className="dj1948-card-value">{socios.length}</span>
                        <span className="dj1948-card-label">Socios registrados</span>
                        <span className="dj1948-card-detail">{totalAcciones} acciones totales</span>
                    </div>
                </div>

                <div className="dj1948-card">
                    <div className="dj1948-card-icon dj1948-card-icon--green"><FaMoneyBillWave /></div>
                    <div className="dj1948-card-info">
                        <span className="dj1948-card-value">{dividendos.length}</span>
                        <span className="dj1948-card-label">Retiros / Dividendos</span>
                        <span className="dj1948-card-detail">
                            C5: {formatNumber(totalMontoC5)} | C8: {formatNumber(totalMontoC8)}
                        </span>
                    </div>
                </div>

                <div className="dj1948-card">
                    <div className="dj1948-card-icon dj1948-card-icon--orange"><FaExclamationTriangle /></div>
                    <div className="dj1948-card-info">
                        <span className="dj1948-card-value">{retirosExceso.length}</span>
                        <span className="dj1948-card-label">Retiros en exceso</span>
                    </div>
                </div>
            </div>

            {/* Tabla resumen de socios */}
            {socios.length > 0 && (
                <div className="dj1948-section">
                    <h4>Socios</h4>
                    <div className="dj1948-table-wrap">
                        <table className="dj1948-table">
                            <thead>
                                <tr>
                                    <th>RUT</th>
                                    <th>Nombre</th>
                                    <th>Tipo</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {socios.map(s => (
                                    <tr key={s.id}>
                                        <td>{s.rut}</td>
                                        <td>{s.nombre}</td>
                                        <td>{s.tipo_persona === 'juridica' ? 'Jurídica' : 'Natural'}</td>
                                        <td className="text-right">{s.cantidad_acciones || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Tabla resumen de dividendos */}
            {dividendos.length > 0 && (
                <div className="dj1948-section">
                    <h4>Retiros y Dividendos ({year})</h4>
                    <div className="dj1948-table-wrap">
                        <table className="dj1948-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>RUT Receptor</th>
                                    <th>C5 - Trib. cumplida</th>
                                    <th>C8 - Afectos IGC/IA</th>
                                    <th>Certificado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dividendos.map(d => (
                                    <tr key={d.id}>
                                        <td>{d.fecha_retiro ? new Date(d.fecha_retiro).toLocaleDateString('es-CL') : '-'}</td>
                                        <td>{d.rut_receptor}</td>
                                        <td className="text-right">{formatNumber(d.monto_c5)}</td>
                                        <td className="text-right">{formatNumber(d.monto_c8)}</td>
                                        <td>{d.numero_certificado_c33 || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Botón de descarga */}
            <div className="dj1948-actions">
                <button
                    className="dj1948-btn-csv"
                    onClick={handleDownloadCSV}
                    disabled={downloading || socios.length === 0}
                >
                    <FaFileDownload />
                    {downloading ? 'Generando...' : 'Descargar CSV DJ1948'}
                </button>
                {socios.length === 0 && (
                    <p className="dj1948-warning">
                        Debe registrar socios en la sección "Socios empresa y Dividendos" para poder generar el CSV.
                    </p>
                )}
            </div>
        </div>
    );
};

export default DJ1948RetirosDividendosTab;
